import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class ChangeDetectionService {

  constructor() { }

  /**
   * Detects if there are any changes between original and current values
   * @param originalValues - The original/initial values object
   * @param currentValues - The current values object
   * @param fieldsToCompare - Optional array of specific fields to compare. If not provided, compares all fields.
   * @returns boolean - true if changes detected, false otherwise
   */
  hasChanges(
    originalValues: any, 
    currentValues: any, 
    fieldsToCompare?: string[]
  ): boolean {
    if (!originalValues || !currentValues) {
      console.warn('ChangeDetectionService: originalValues or currentValues is null/undefined');
      return false;
    }

    // If specific fields provided, only compare those
    const fields = fieldsToCompare || Object.keys(originalValues);

    for (const field of fields) {
      const originalValue = originalValues[field];
      const currentValue = currentValues[field];

      if (!this.isEqual(originalValue, currentValue)) {
        // console.log(`Change detected in field "${field}":`, {
        //   original: originalValue,
        //   current: currentValue
        // });
        return true;
      }
    }

    return false;
  }

  /**
   * Gets a list of fields that have changed
   * @param originalValues - The original/initial values object
   * @param currentValues - The current values object
   * @param fieldsToCompare - Optional array of specific fields to compare
   * @returns string[] - Array of field names that have changed
   */
  getChangedFields(
    originalValues: any, 
    currentValues: any, 
    fieldsToCompare?: string[]
  ): string[] {
    if (!originalValues || !currentValues) {
      return [];
    }

    const changedFields: string[] = [];
    const fields = fieldsToCompare || Object.keys(originalValues);

    for (const field of fields) {
      const originalValue = originalValues[field];
      const currentValue = currentValues[field];

      if (!this.isEqual(originalValue, currentValue)) {
        changedFields.push(field);
      }
    }

    return changedFields;
  }

  /**
   * Creates a deep copy of an object to store as original values
   * @param obj - Object to clone
   * @returns Cloned object
   */
  cloneValues(obj: any): any {
    if (obj === null || obj === undefined) {
      return obj;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => this.cloneValues(item));
    }
    
    if (typeof obj === 'object') {
      const cloned: any = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          cloned[key] = this.cloneValues(obj[key]);
        }
      }
      return cloned;
    }
    
    return obj;
  }

  /**
   * Compares two values for equality
   * Handles primitives, arrays, and objects
   */
  private isEqual(value1: any, value2: any): boolean {
    // Handle null/undefined
    if (value1 === value2) {
      return true;
    }
    if (value1 == null || value2 == null) {
      return false;
    }

    // Handle arrays
    if (Array.isArray(value1) && Array.isArray(value2)) {
      return this.areArraysEqual(value1, value2);
    }

    // Handle objects
    if (typeof value1 === 'object' && typeof value2 === 'object') {
      return this.areObjectsEqual(value1, value2);
    }

    // Handle primitives
    return value1 === value2;
  }

  /**
   * Compares two arrays for equality
   * Sorts arrays before comparison to handle order differences
   */
  private areArraysEqual(arr1: any[], arr2: any[]): boolean {
    if (arr1.length !== arr2.length){ 
      return false;
    }

    const sorted1 = [...arr1].sort();
    const sorted2 = [...arr2].sort();

    for (let i = 0; i < sorted1.length; i++) {
      if (!this.isEqual(sorted1[i], sorted2[i])) {
        return false;
      }
    }

    return true;
  }

  /**
   * Compares two objects for equality
   */
  private areObjectsEqual(obj1: any, obj2: any): boolean {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length){
       return false;
      }

    for (const key of keys1) {
      if (!keys2.includes(key)){ 
        return false;
      }
      if (!this.isEqual(obj1[key], obj2[key])){ 
        return false;
      }
    }

    return true;
  }
}
